/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/** Common Components */
import MolGrid from "../../components/grid/MolGrid";
import CardSection from "../../components/ui/card/CardSection";

/** Custom Hooks */
import useDebounce from "../../app/customHooks/useDebounce";

/** Common Services & Data Files */
import { encryptUrlData } from "../../services/CryptoService";
import SwalAlert from "../../services/swalService/SwalService";
import ToastService from "../../services/toastService/ToastService";

/** RTK Query */
import {
  useDeleteTeamMutation,
  useGetTeamListMutation,
} from "../../app/services/teamAPI";

/** Configuration Files */
import { TeamsGridConfig } from "./config/Teams.data";

/** CSS Files */
import "./Teams.scss";
import Iconify from "../../layouts/components/iconify/iconify";
import Pagination from "../../components/ui/pagination/Pagination";
import AvatarGroup from "../../components/ui/avatarGroup/AvatarGroup";


const staticAvatars = [
  { src: "/path/to/avatar1.jpg", alt: "Avatar 1", label: "Avatar 1" },
  { src: "/path/to/avatar2.jpg", alt: "Avatar 2", label: "Avatar 2" },
  { src: "/path/to/avatar2.jpg", alt: "Avatar 3", label: "Avatar 3" },
  { src: "/path/to/avatar2.jpg", alt: "Avatar 4", label: "Avatar 4" },
  { src: "/path/to/avatar2.jpg", alt: "Avatar 5", label: "Avatar 5" },
  { src: "/path/to/avatar2.jpg", alt: "Avatar 6", label: "Avatar 6" },
];

const teamData = [
  {
    teamId: 1,
    teamName: "UI Team",
    responsibleUser: "Alice Johnson",
    isActive: "Active",
  },
  {
    teamId: 2,
    teamName: "Clixz Team",
    responsibleUser: "John Smith",
    isActive: "Inactive",
  },
  {
    teamId: 3,
    teamName: "Total Scope Team",
    responsibleUser: "Karen Adams",
    isActive: "Active",
  },
  {
    teamId: 4,
    teamName: "Echem Team",
    responsibleUser: "Chris Lee",
    isActive: "Inactive",
  },
  {
    teamId: 5,
    teamName: "Flying Cape Team",
    responsibleUser: "Mary Davis",
    isActive: "Active",
  },
];

const Teams = () => {

  const molGridRef = useRef();
  const navigate = useNavigate();
  const { confirm } = SwalAlert();

  const [searchText, setSearchText] = useState("");
  const [teamGridData, setTeamGridData] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [pageSize] = useState(10);

  const debouncedSearch = useDebounce(searchText, 300);

  const [viewType, setViewType] = useState("list");
  const showListView = () => setViewType("list");
  const showCardView = () => setViewType("card");

  const [getTeamList, {
    isLoading: isTeamListLoading,
    isSuccess: isTeamListSuccess,
    data: isTeamListData,
  },
  ] = useGetTeamListMutation();

  const [deleteTeam, { isSuccess: isTeamsDeleted, data: deleteTeamsData }] =
    useDeleteTeamMutation();

  useEffect(() => {
    if (isTeamsDeleted && deleteTeamsData) {
      ToastService.success(deleteTeamsData.errorMessage);
      getTeamDataList(molGridRef.current.getCurrentPageObject());
    }
  }, [isTeamsDeleted && deleteTeamsData]);

  const addTeams = (data) => {
    navigate("/admin/AddTeam");
  };
  const getTeamDataList = (pageObject) => {
    const request = {
      pagination: {
        pageNumber: pageObject.pageNumber,
        pageSize: pageObject.pageSize,
      },
      filters: {
        searchText: debouncedSearch,
      },
    };
    getTeamList(request);
  };

  useEffect(() => {
    if (isTeamListSuccess && isTeamListData) {
      if (isTeamListData) {
        setTeamGridData(isTeamListData.dataSource);
      }
      if (isTeamListData.totalRecord) {
        setTotalRowCount(isTeamListData.totalRecord);
      }
    }
  }, [isTeamListSuccess, isTeamListData]);

  useEffect(() => {
    if (molGridRef.current) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      const request = {
        pagination: {
          pageNumber: currentPageObject.pageNumber,
          pageSize: currentPageObject.pageSize,
        },
        filters: { searchText: debouncedSearch },
      };
      getLists(request);
    }
  }, [debouncedSearch]);

  const getLists = (pageObject) => {
    getTeamList(pageObject);
  };

  const handlePageChange = (page) => {
    const request = {
      pagination: { pageNumber: page.pageNumber, pageSize: page.pageSize },
      filters: { searchText: debouncedSearch },
    };
    getLists(request);
  };
  const handleOnSearchChange = (e) => {
    let value = e.target.value;
    setSearchText(value);
  };

  const onEditAction = (data) => {
    navigate(`/admin/teams/EditTeam/${encryptUrlData({ teamId: data.teamId, teamName: data.teamName })}`);
  };

  const onDeleteClick = (data) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteTeam(data.teamId);
      }
    });
  };

  const actionHandler = {
    EDIT: onEditAction,
    DELETE: onDeleteClick,
  };

  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleDropdown = (teamId) => {
    setActiveDropdown((prev) => (prev === teamId ? null : teamId));
  };

  return (
    <div className="team-list center-top-space">
      <div className="list-view-section">
        <div className="table-grid">
          <div className="project-list">
            <CardSection
              isContentLoading={isTeamListLoading}
              cardTitle={"Team List"}
              rightButton={true}
              buttonClassName="theme-btn"
              titleButtonClick={addTeams}
              buttonText="Team"
              textWithIcon={true}
              iconName="material-symbols:add-rounded"
              fontIcon={true}
              handleChange={handleOnSearchChange}
              searchInputName="Search by Team Name"
              searchInput={true}
              showListView={showListView}
              showCardView={showCardView}
              viewType={viewType}
            >
              {viewType === "card" ? (
                <div className="card-view">
                  <div className="row">
                    {teamData.map((team) => {
                      const statusClass =
                        team.isActive === "Active" ? "active" : "inactive";

                      return (
                        <div
                          className="col-xxl-3 col-xl-4 col-md-6 col-12"
                          key={team.teamId}
                        >
                          <div className="team-card">

                            {/* Card Header */}
                            <div className="card-header">
                              <h5 className="team-title">
                                {team.teamName}
                              </h5>
                              <div>
                                <div
                                  className={`status-label ${statusClass}`}
                                >
                                  {team.isActive === "Active"
                                    ? "Active"
                                    : "Inactive"}
                                </div>

                                {/* Three-dot menu */}
                                <div
                                  className="menu-container"
                                  ref={dropdownRef}
                                >
                                  <button
                                    className="menu-icon"
                                    onClick={() =>
                                      toggleDropdown(team.teamId)
                                    }
                                  >
                                    <Iconify icon="ph:dots-three-outline-vertical-fill" />
                                  </button>
                                  {activeDropdown === team.teamId && (
                                    <div className="dropdown-menu">
                                      <ul>
                                        <li
                                          onClick={() => console.log("Edit clicked")}
                                        >
                                          <Iconify icon="line-md:pencil" />{" "}
                                          Edit
                                        </li>
                                        <li
                                          onClick={() => console.log("Delete clicked")}
                                        >
                                          <Iconify icon="mingcute:delete-2-line" />{" "}
                                          Delete
                                        </li>
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Tags */}
                            <div className="team-tags">
                              <span className="tag">UI</span>
                              <span className="tag">Angular</span>
                              <span className="tag">Php</span>
                            </div>

                            {/* Avatars */}
                            <div className="teams-avatar-group">
                              <AvatarGroup
                                avatars={staticAvatars}
                                max={3}
                                size="small"
                                shape="circle"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <MolGrid
                  ref={molGridRef}
                  configuration={TeamsGridConfig}
                  dataSource={teamGridData}
                  isLoading={isTeamListLoading}
                  allowPagination={true}
                  pagination={{
                    totalCount: totalRowCount,
                    pageSize: 20,
                    currentPage: 1,
                  }}
                  onPageChange={handlePageChange}
                  onActionChange={actionHandler}
                />
              )}
            </CardSection>
            {viewType === "card" ? (
              <div className="pagination-container">
                <Pagination
                  pageSize={pageSize}
                  totalCount={totalRowCount}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teams;
